import { ListFilter, X } from '@orgus/ui/icons';
import type { AuditFacet } from '@orgus/protocol';
import { Button, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger, IconButton } from '@orgus/ui';

/**
 * A multi-select over one audit field (actor, action, machine), with how many entries each
 * value has under the other filters. Values selected but absent from the facets stay listed.
 */
export function FacetPicker({
  name,
  heading,
  facets,
  selected,
  onChange,
  label,
}: {
  /** "Action", "By", "Machine": the button text when nothing is chosen is "Any <name>". */
  name: string;
  heading: string;
  facets: AuditFacet[];
  selected: string[];
  onChange: (next: string[]) => void;
  label: (value: string) => string;
}) {
  const values = [...facets];
  for (const s of selected) if (!values.some((f) => f.value === s)) values.push({ value: s, count: 0 });
  const text = !selected.length ? `Any ${name.toLowerCase()}` : selected.length === 1 ? label(selected[0]!) : `${selected.length} ${name.toLowerCase()}s`;
  return (
    <span className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant={selected.length ? 'secondary' : 'ghost'} icon={<ListFilter />} aria-label={`Filter by ${name.toLowerCase()}: ${text}`}>
            <span className="max-w-40 truncate">{text}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          <DropdownMenuLabel>{heading}</DropdownMenuLabel>
          {values.length === 0 && <div className="px-2 py-2 text-ui text-fg-subtle">Nothing to choose from yet</div>}
          {values.map((f) => (
            <DropdownMenuCheckboxItem
              key={f.value}
              checked={selected.includes(f.value)}
              onSelect={(ev) => ev.preventDefault()}
              onCheckedChange={(on) => onChange(on ? [...selected, f.value] : selected.filter((x) => x !== f.value))}
            >
              <span className="flex min-w-0 flex-1 items-center justify-between gap-4">
                <span className="truncate">{label(f.value)}</span>
                <span className="text-fg-subtle num">{f.count}</span>
              </span>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {selected.length > 0 && <IconButton label={`Clear the ${name.toLowerCase()} filter`} icon={<X />} size="xs" onClick={() => onChange([])} />}
    </span>
  );
}
